import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChartBarIcon,
  HeartIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  BriefcaseIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ClockIcon,
  ShieldCheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  FilmIcon,
  VideoCameraIcon,
  EllipsisHorizontalIcon,
  SparklesIcon,
  LightBulbIcon,
  DocumentChartBarIcon,
} from '@heroicons/react/24/outline';
import { setPreference } from '../../store/slices/themeSlice';
import { logout, updateUser } from '../../store/slices/authSlice';
import BrandLogo, { brandAsset } from '../shared/BrandLogo';
import { trackLogout } from '../../utils/analytics';
import { canAccessFeature } from '../../utils/tierAccess';
import { mostSpecificNavMatch } from '../../utils/navActive';
import { useFeature } from '../../hooks/useFeature';
import NotificationsCenter from '../shared/NotificationsCenter';
import OnboardingWizard from '../shared/OnboardingWizard';
import MoreSheet from './MoreSheet';
import toast from 'react-hot-toast';

// ── Nav items ──────────────────────────────────────────────────────────────

const BASE_NAV = [
  { name: 'Analytics',       href: '/dashboard/analytics',       icon: ChartBarIcon,      mobileLabel: 'Analytics',  featureKey: 'analytics' },
  { name: 'Content Analytics', href: '/dashboard/video-analytics', icon: FilmIcon,          mobileLabel: 'Insights',   featureKey: 'video-analytics' },
  { name: 'Health Monitor',  href: '/dashboard/health',          icon: HeartIcon,         mobileLabel: 'Health',     featureKey: 'health' },
  { name: 'Content',        href: '/dashboard/content',   icon: DocumentTextIcon,   mobileLabel: 'Content',    featureKey: 'content' },
  { name: 'Video Editor',  href: '/dashboard/editor',    icon: VideoCameraIcon,    mobileLabel: 'Editor',     featureKey: 'editor' },
  { name: 'Revenue',        href: '/dashboard/revenue',   icon: CurrencyDollarIcon, mobileLabel: 'Revenue',    featureKey: 'revenue' },
  { name: 'Deals',          href: '/dashboard/deals',     icon: BriefcaseIcon,      mobileLabel: 'Deals',      featureKey: 'deals' },
];

// Settings is appended LAST in the nav list (after the tier/flag/admin items) so it always
// sits at the end of the menu — a settings item in the middle of the list is poor UX.
const SETTINGS_NAV = { name: 'Settings', href: '/dashboard/settings', icon: Cog6ToothIcon, mobileLabel: 'Settings', featureKey: 'settings' };

const ADMIN_NAV = [
  { name: 'Web Analytics', href: '/dashboard/web-analytics', icon: GlobeAltIcon,   badge: 'Admin' },
  { name: 'Admin',         href: '/dashboard/admin',          icon: ShieldCheckIcon, badge: 'Admin' },
];

// Gated by the creator_intelligence feature flag (useFeature), not by tier — so it
// appears only for users the flag targets (allow-list / role / tier / rollout).
const INTELLIGENCE_NAV = { name: 'Intelligence', href: '/dashboard/intelligence', icon: SparklesIcon, mobileLabel: 'DNA', badge: 'Beta' };
const IDEA_STUDIO_NAV = { name: 'Idea Studio', href: '/dashboard/idea-studio', icon: LightBulbIcon, mobileLabel: 'Ideas', badge: 'Beta' };
const WEEKLY_REPORT_NAV = { name: 'Weekly Report', href: '/dashboard/intelligence/weekly', icon: DocumentChartBarIcon, mobileLabel: 'Report', badge: 'Beta' };

// Bottom nav is dynamically filtered inside the component based on tier.
// Fixed slot order for the mobile bottom nav (max 4 primary tabs + "More").
// If a primary item is tier-hidden its slot is simply dropped — other items
// are never re-ordered into it.
const MOBILE_PRIMARY_KEYS = ['analytics', 'content', 'editor', 'video-analytics'];

// ── UserInitials helper ────────────────────────────────────────────────────

const UserInitials = ({ user }) => {
  const name = user?.displayName || user?.name || 'U';
  return <>{name.charAt(0).toUpperCase()}</>;
};

// ── DashboardLayout ────────────────────────────────────────────────────────

const DashboardLayout = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const { preference } = useSelector((state) => state.theme);
  // Header theme button cycles Auto → Light → Dark → Auto.
  const nextThemePref = { auto: 'light', light: 'dark', dark: 'auto' };
  const themeTitle = { auto: 'Theme: Auto (light by day, dark at night)', light: 'Theme: Light', dark: 'Theme: Dark' };
  const { user }   = useSelector((state) => state.auth);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [showOnboarding, setShowOnboarding]   = useState(false);
  const [showAvatarMenu, setShowAvatarMenu]   = useState(false);
  const [showMoreSheet, setShowMoreSheet]     = useState(false);
  const avatarMenuRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (user && !user.onboardingCompleted) {
      if (!localStorage.getItem('onboarding_completed')) setShowOnboarding(true);
    }
  }, [user]);

  // Close avatar menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(e.target)) {
        setShowAvatarMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isAdmin = user?.role === 'ADMIN' || user?.roles?.some((r) => r.name === 'ADMIN' || r === 'ADMIN');
  const userTier = user?.tier || 'FREE';
  const { enabled: intelligenceEnabled } = useFeature('creator_intelligence');
  const tierFilteredNav = BASE_NAV.filter((item) => canAccessFeature(userTier, item.featureKey, isAdmin));
  const navigation = [
    ...tierFilteredNav,
    ...(intelligenceEnabled ? [INTELLIGENCE_NAV, IDEA_STUDIO_NAV, WEEKLY_REPORT_NAV] : []),
    ...(isAdmin ? ADMIN_NAV : []),
    SETTINGS_NAV, // always last — keep Settings at the end of the menu
  ];

  // Mobile bottom nav: fixed slots (tier-hidden slots drop out, no re-ordering)
  const mobilePrimaryTabs = MOBILE_PRIMARY_KEYS
    .map((key) => tierFilteredNav.find((item) => item.featureKey === key))
    .filter(Boolean);
  // Everything else that's gated-visible lives in the "More" sheet
  const moreSheetItems = navigation.filter(
    (item) => !MOBILE_PRIMARY_KEYS.includes(item.featureKey)
  );

  const handleLogout = async () => {
    setShowAvatarMenu(false);
    trackLogout();
    await dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    localStorage.setItem('onboarding_completed', 'true');
    dispatch(updateUser({ onboardingCompleted: true }));
  };

  // Active = the MOST SPECIFIC matching nav item. A plain prefix match would light up a parent
  // route (/dashboard/intelligence) on a child route (/dashboard/intelligence/weekly), so we pick
  // only the longest matching href — the parent stays highlighted for genuine sub-pages that
  // aren't their own nav item, but a sibling nav item wins on its exact route.
  const bestMatchHref = mostSpecificNavMatch(location.pathname, navigation.map((item) => item.href));
  const isActive = (path) => path === bestMatchHref;

  // "More" tab is active when the current route belongs to a sheet item
  const moreTabActive = moreSheetItems.some((item) => isActive(item.href));

  // Safety net: close the sheet on any route change
  useEffect(() => {
    setShowMoreSheet(false);
  }, [location.pathname]);

  const currentPageName =
    navigation.find((item) => isActive(item.href))?.name || 'Dashboard';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">

      {/* ── Sidebar (md+) ─────────────────────────────────────────────────── */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full hidden md:flex flex-col
        bg-white dark:bg-dark-900
        border-r border-gray-100 dark:border-dark-800
        shadow-sm
        transition-[width] duration-300 ease-in-out
        ${sidebarCollapsed ? 'w-20' : 'w-64'}
      `}>

        {/* Logo */}
        <div className={`flex items-center border-b border-gray-100 dark:border-dark-800 transition-all duration-300 ${
          sidebarCollapsed ? 'justify-center px-4 py-5' : 'justify-between px-5 py-5'
        }`}>
          <Link to="/dashboard" className="flex items-center min-w-0 group">
            {sidebarCollapsed ? (
              // Collapsed: lens symbol only
              <img
                src={brandAsset.symbol(128)}
                alt="OmniConnX"
                className="flex-shrink-0 w-9 h-9 group-hover:scale-105 transition-transform duration-200"
              />
            ) : (
              // Expanded: official v2 lockup (lens replaces the O) — same as auth/marketing
              <BrandLogo size="sm" />
            )}
          </Link>

          {/* Collapse toggle */}
          {!sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(true)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
          )}
          {sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="absolute -right-3 top-16 w-6 h-6 rounded-full bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 shadow-sm flex items-center justify-center text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 transition-colors"
              aria-label="Expand sidebar"
            >
              <ChevronRightIcon className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {navigation.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group/item relative flex items-center rounded-xl transition-all duration-200 ${
                  sidebarCollapsed ? 'justify-center px-2 py-3' : 'gap-3 px-3 py-2.5'
                } ${
                  active
                    ? 'bg-gradient-to-r from-primary-50 to-transparent dark:from-primary-900/25 dark:to-transparent text-primary-700 dark:text-primary-400 border-l-[3px] border-primary-500'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/60 hover:text-gray-900 dark:hover:text-gray-200 border-l-[3px] border-transparent'
                }`}
              >
                <item.icon className={`flex-shrink-0 w-[18px] h-[18px] ${
                  active ? 'text-primary-600 dark:text-primary-400' : ''
                }`} />

                <span className={`font-medium text-sm whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-300 ease-in-out ${
                  sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100'
                }`}>
                  {item.name}
                </span>

                {item.badge && !sidebarCollapsed && (
                  <span className="ml-auto text-[10px] font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 px-1.5 py-0.5 rounded-full flex-shrink-0">
                    {item.badge}
                  </span>
                )}

                {/* Tooltip (collapsed only) */}
                <span className={`
                  pointer-events-none absolute left-full ml-3 z-[200]
                  px-3 py-2 rounded-xl shadow-lg
                  bg-gray-900 dark:bg-dark-700 text-white text-sm font-medium whitespace-nowrap
                  flex items-center gap-2
                  transition-[opacity,visibility] duration-150
                  ${sidebarCollapsed
                    ? 'opacity-0 invisible group-hover/item:opacity-100 group-hover/item:visible'
                    : 'opacity-0 invisible'
                  }
                `}>
                  <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-dark-700" />
                  {item.name}
                  {item.badge && (
                    <span className="text-xs bg-primary-500 text-white px-1.5 py-0.5 rounded-md">
                      {item.badge}
                    </span>
                  )}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-gray-100 dark:border-dark-800 space-y-1">
          <div className={`flex items-center rounded-xl bg-gray-50 dark:bg-dark-800/80 transition-all duration-300 ${
            sidebarCollapsed ? 'justify-center p-2.5' : 'gap-3 p-3'
          }`}>
            <div
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm ring-2 ring-white dark:ring-dark-800"
              style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}
            >
              <UserInitials user={user} />
            </div>
            <div className={`min-w-0 overflow-hidden transition-[max-width,opacity] duration-300 ease-in-out ${
              sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[160px] opacity-100 flex-1'
            }`}>
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user?.displayName || user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                {user?.email || ''}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className={`group/logout relative w-full flex items-center rounded-xl text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 ${
              sidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-2.5 px-3 py-2.5'
            }`}
          >
            <ArrowRightOnRectangleIcon className="w-[18px] h-[18px] flex-shrink-0" />
            <span className={`text-sm font-medium whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-300 ease-in-out ${
              sidebarCollapsed ? 'max-w-0 opacity-0' : 'max-w-[200px] opacity-100'
            }`}>
              Logout
            </span>
            <span className={`
              pointer-events-none absolute left-full ml-3 z-[200]
              px-3 py-2 rounded-xl shadow-lg
              bg-gray-900 dark:bg-dark-700 text-white text-sm font-medium whitespace-nowrap
              transition-[opacity,visibility] duration-150
              ${sidebarCollapsed
                ? 'opacity-0 invisible group-hover/logout:opacity-100 group-hover/logout:visible'
                : 'opacity-0 invisible'
              }
            `}>
              <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-dark-700" />
              Logout
            </span>
          </button>
        </div>
      </aside>

      {/* ── Main content ──────────────────────────────────────────────────────── */}
      <div className={`transition-[padding] duration-300 ease-in-out
        ${sidebarCollapsed ? 'md:pl-20' : 'md:pl-64'}
        pb-20 md:pb-0
      `}>

        {/* ── Top header ──────────────────────────────────────────────────── */}
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-dark-900/95 backdrop-blur-sm border-b border-gray-100 dark:border-dark-800">
          <div className="flex items-center gap-3 px-4 py-3 md:px-6">

            {/* Mobile logo — official v2 lockup (lens replaces the O) */}
            <Link to="/dashboard" className="md:hidden flex items-center">
              <BrandLogo size="sm" />
            </Link>

            {/* Desktop page title */}
            <h1 className="hidden md:block text-xl font-display font-bold text-gray-900 dark:text-white flex-1">
              {currentPageName}
            </h1>

            {/* Right actions — push to end on mobile */}
            <div className="flex items-center gap-1 ml-auto">
              <NotificationsCenter />

              <button
                onClick={() => dispatch(setPreference(nextThemePref[preference] || 'auto'))}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-800 transition-colors"
                aria-label="Change theme"
                title={themeTitle[preference]}
              >
                {preference === 'auto'
                  ? <ClockIcon className="w-5 h-5 text-gray-400" />
                  : preference === 'light'
                    ? <SunIcon className="w-5 h-5 text-amber-400" />
                    : <MoonIcon className="w-5 h-5 text-gray-400" />}
              </button>

              {/* Mobile avatar menu */}
              <div className="relative md:hidden" ref={avatarMenuRef}>
                <button
                  onClick={() => setShowAvatarMenu((v) => !v)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm ring-2 ring-white dark:ring-dark-800"
                  style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}
                >
                  <UserInitials user={user} />
                </button>

                <AnimatePresence>
                  {showAvatarMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -4 }}
                      transition={{ duration: 0.12 }}
                      className="absolute right-0 top-full mt-2 w-52 card-social shadow-xl shadow-gray-200/60 dark:shadow-dark-950/60 z-50 overflow-hidden"
                    >
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {user?.displayName || user?.name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/dashboard/settings"
                          onClick={() => setShowAvatarMenu(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
                        >
                          <Cog6ToothIcon className="w-4 h-4" />
                          Settings
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <ArrowRightOnRectangleIcon className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* ── Page content (soft transition on route change) ────────────────── */}
        <main className="p-4 md:p-6 lg:p-8">
          {/* Opacity-only fade — deliberately no transform, so this wrapper
              never becomes a containing block for position:fixed descendants
              (dropdowns, modals) inside the routed pages. */}
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/* ── Mobile bottom navigation ──────────────────────────────────────────── */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 dark:bg-dark-900/95 backdrop-blur-sm border-t border-gray-100 dark:border-dark-800 pb-safe">
        <div className="flex items-stretch h-16">
          {mobilePrimaryTabs.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className="bottom-nav-tab relative"
              >
                {active && (
                  <motion.span
                    layoutId="bottom-nav-indicator"
                    className="absolute top-0 left-2 right-2 h-0.5 rounded-full bg-gradient-to-r from-primary-600 to-primary-400"
                  />
                )}
                <item.icon className={`w-6 h-6 transition-colors duration-150 ${
                  active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                }`} />
                <span className={`text-[10px] font-semibold tracking-wide transition-colors duration-150 ${
                  active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {item.mobileLabel}
                </span>
              </Link>
            );
          })}

          {/* "More" tab — opens the bottom sheet with remaining nav items */}
          <button
            type="button"
            onClick={() => setShowMoreSheet(true)}
            className="bottom-nav-tab relative"
            aria-label="More navigation options"
            aria-expanded={showMoreSheet}
          >
            {moreTabActive && (
              <motion.span
                layoutId="bottom-nav-indicator"
                className="absolute top-0 left-2 right-2 h-0.5 rounded-full bg-gradient-to-r from-primary-600 to-primary-400"
              />
            )}
            <EllipsisHorizontalIcon className={`w-6 h-6 transition-colors duration-150 ${
              moreTabActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
            }`} />
            <span className={`text-[10px] font-semibold tracking-wide transition-colors duration-150 ${
              moreTabActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
            }`}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* ── More bottom sheet (mobile) ────────────────────────────────────────── */}
      <MoreSheet
        open={showMoreSheet}
        onClose={() => setShowMoreSheet(false)}
        items={moreSheetItems}
        isActive={isActive}
      />

      {showOnboarding && (
        <OnboardingWizard onComplete={handleOnboardingComplete} />
      )}
    </div>
  );
};

export default DashboardLayout;
